/**
 * Fiscal Printer - Web Serial API connection management + monitoring.
 *
 * Poza samym otwieraniem portu ten modul utrzymuje "zywe" polaczenie:
 *  - nasluch zdarzen serial disconnect/connect (wypiecie/wpiecie USB),
 *  - lekki HEARTBEAT <dle/> co ~7s TYLKO gdy port jest bezczynny (nie w trakcie druku),
 *  - automatyczny reconnect na zapamietany port (navigator.serial.getPorts()),
 *  - eksport STANU POLACZENIA (getConnectionStatus / subscribeConnection) dla UI.
 *
 * Port w Web Serial jest zasobem WYLACZNYM: w trakcie druku heartbeat MUSI byc
 * wstrzymany (beginPrinting/endPrinting), inaczej reader/writer sie zderza.
 */
import { SERIAL_PORT_OPTIONS } from './constants';
import { sendXML, drainPort } from './transport';

const HEARTBEAT_INTERVAL_MS = 7000;

// Wlasny dekoder na potrzeby heartbeatu (nie importujemy receiptXML zeby uniknac cyklu).
const hbDecoder = (typeof TextDecoder !== 'undefined') ? new TextDecoder('windows-1250') : null;

const mon = {
  port: null,
  heartbeatTimer: null,
  beatPromise: null,
  printing: false,     // true w trakcie druku -> heartbeat wstrzymany
  reconnecting: false,
  disconnectHandler: null,
  connectHandler: null,
  status: {
    connected: false,
    writable: false,
    reconnecting: false,
    lastBeat: 0,
    error: null,
  },
};

const listeners = new Set();

function delay(ms) { return new Promise(r => setTimeout(r, ms)); }

function setStatus(patch) {
  mon.status = { ...mon.status, ...patch };
  // Snapshot niesie AKTUALNY obiekt portu — po reconnect (Chrome potrafi dac
  // NOWY obiekt SerialPort) serwis synchronizuje swoje this.port z tego pola.
  const snapshot = { ...mon.status, port: mon.port };
  for (const fn of listeners) {
    try { fn(snapshot); } catch (_) { /* listener errors nie moga wywrocic monitoringu */ }
  }
}

/**
 * Aktualny stan polaczenia (snapshot). Dla UI. Zawiera tez aktualny port.
 */
export function getConnectionStatus() {
  return { ...mon.status, port: mon.port };
}

/**
 * Aktualnie monitorowany port (moze byc NOWY obiekt po reconnect).
 */
export function getCurrentPort() {
  return mon.port;
}

/**
 * Subskrypcja zmian stanu polaczenia. Woluje `fn` natychmiast z biezacym stanem
 * oraz przy kazdej zmianie. Zwraca funkcje odsubskrybowania.
 */
export function subscribeConnection(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  try { fn({ ...mon.status }); } catch (_) {}
  return () => { listeners.delete(fn); };
}

/**
 * Wstrzymaj heartbeat na czas druku (port jest zasobem wylacznym).
 * Czeka na ewentualny heartbeat w locie, zeby nie zderzyc reader/writer.
 */
export async function beginPrinting() {
  mon.printing = true;
  if (mon.beatPromise) {
    try { await mon.beatPromise; } catch (_) {}
  }
}

/**
 * Wznow heartbeat po zakonczeniu druku.
 */
export function endPrinting() {
  mon.printing = false;
}

// ---- Heartbeat ----------------------------------------------------------

async function heartbeatTick() {
  if (mon.printing || mon.reconnecting || !mon.port) return;
  const port = mon.port;

  // Heartbeat PASYWNY: NIE wysylamy nic do portu. Samo wypiecie USB zeruje
  // readable/writable (Chrome) + osobne zdarzenie 'disconnect', wiec do wykrycia
  // rozlaczenia nie trzeba nic pisac. Wczesniej heartbeat slal <dle/>, na ktore
  // Deon odpowiada z ~2 s opoznieniem - to zajmowalo port (reader/writer) i
  // spozniona odpowiedz wpadala w druk paragonu => paragon "nie szedl" na
  // drukarke / status "niezafiskalizowany". Zero cyklicznego I/O = zero kolizji.
  if (!port.readable || !port.writable) {
    setStatus({ connected: false, writable: false });
    await tryReconnect('heartbeat-port-not-ready');
    return;
  }
  setStatus({ connected: true, writable: true, lastBeat: Date.now(), error: null });
}

function scheduleBeat() {
  if (mon.printing || mon.reconnecting || !mon.port || mon.beatPromise) return;
  mon.beatPromise = heartbeatTick().finally(() => { mon.beatPromise = null; });
}

function startHeartbeat() {
  if (mon.heartbeatTimer) return;
  mon.heartbeatTimer = setInterval(scheduleBeat, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat() {
  if (mon.heartbeatTimer) {
    clearInterval(mon.heartbeatTimer);
    mon.heartbeatTimer = null;
  }
}

// ---- Reconnect ----------------------------------------------------------

/**
 * Upewnij sie, ze `port` (lub aktualnie monitorowany) jest otwarty i zapisywalny.
 * Wykorzystuje fakt, ze navigator.serial.getPorts() PAMIETA nadany port -
 * mozna go otworzyc ponownie BEZ pytania uzytkownika. Zwraca gotowy port albo null.
 *
 * W Web Serial obiekt SerialPort jest reuzywany po ponownym wpieciu tego samego
 * urzadzenia, wiec zwrocony port to zwykle TEN SAM obiekt - referencje w
 * fiscalPrinterService.port pozostaja wazne.
 */
// Zamknij "martwy" port (np. po wypieciu USB) zeby zwolnic uchwyt urzadzenia.
// Inaczej ponowne open() na nowym obiekcie tego samego urzadzenia bywa odrzucone
// ("urzadzenie zajete") - to czesta przyczyna, ze replug nie lapie polaczenia.
async function closeQuietly(p) {
  if (!p) return;
  try { await p.close(); } catch (_) { /* i tak zwalniamy referencje */ }
}

const USB_KEY = 'fiscal_printer_usb';

/**
 * Tozsamosc USB portu: "VID:PID" (np. "1155:22336") albo null gdy port nie jest
 * urzadzeniem USB (np. wbudowany/wirtualny COM). To fizyczny "odcisk palca"
 * drukarki - ten sam sprzet ma ten sam VID:PID niezaleznie od numeru COM.
 */
function portUsbKey(port) {
  try {
    const info = port && port.getInfo ? port.getInfo() : null;
    if (info && info.usbVendorId != null && info.usbProductId != null) {
      return `${info.usbVendorId}:${info.usbProductId}`;
    }
  } catch (_) { /* getInfo niedostepne */ }
  return null;
}

async function openPortIfNeeded(p, staleToClose) {
  if (staleToClose && staleToClose !== p) {
    await closeQuietly(staleToClose);
  }
  if (p.readable && p.writable) return true;
  try {
    if (p.readable || p.writable) {
      await p.close();
      await delay(300);
    }
  } catch (_) { /* ignore */ }
  await p.open(SERIAL_PORT_OPTIONS);
  await delay(200);
  return !!(p.readable && p.writable);
}

// Zapamietaj tozsamosc drukarki (VID:PID) + indeks portu, ktory zadzialal.
function rememberPort(port, idx) {
  try { if (idx != null && idx >= 0) localStorage.setItem('fiscal_printer_port_index', String(idx)); } catch (_) {}
  const key = portUsbKey(port);
  try { if (key) localStorage.setItem(USB_KEY, key); } catch (_) {}
}

/**
 * Odpytaj OTWARTY port komenda statusu <dle/> i sprawdz, czy odpowiada jak
 * drukarka. UWAGA: Deon Online odpowiada dopiero po ~2 s (patrz logi), dlatego
 * okno musi byc szerokie (3 s). idleGap sprawia, ze wracamy zaraz po nadejsciu
 * odpowiedzi, wiec dla realnej drukarki trwa to ~2 s, a nie pelne 3 s.
 * Uzywamy ANGIELSKIEGO <dle/> - polski <dle_pl/> = "nieznany rozkaz" na fw 1.40+.
 */
async function portResponds(port) {
  try {
    if (!port || !port.writable) return false;
    const resp = await sendXML(port, hbDecoder, '<packet><dle/></packet>', true, { responseTimeout: 3000, idleGap: 400 });
    return typeof resp === 'string' && /<dle\b|online\s*=|papererror\s*=|printererror\s*=/i.test(resp);
  } catch (_) {
    // Brak/niepoprawna odpowiedz -> to nie jest drukarka na tym porcie.
    return false;
  }
}

/**
 * Otworz port DRUKARKI. Zwraca otwarty port albo null.
 *
 * Rozpoznanie "COM3 vs COM4" jest DETERMINISTYCZNE (nie zalezy od kaprysnego
 * czasu odpowiedzi drukarki):
 *  1. Znamy TOZSAMOSC USB drukarki (zapisana przy poprzednim polaczeniu) ->
 *     laczymy sie WYLACZNIE z portem o tym samym VID:PID. Brak dopasowania =
 *     portu drukarki nie ma -> null (NIE lapiemy przypadkowego COM3).
 *  2. Jeszcze nie znamy tozsamosci:
 *      - jeden nadany port  -> to on; otworz i naucz sie VID:PID,
 *      - wiele portow       -> ODPYTAJ kazdy (<dle/>, okno 3 s) i wez tego,
 *        ktory ODPOWIADA jak drukarka; zapamietaj jego VID:PID.
 *     Od tego momentu dziala punkt 1 (juz bez odpytywania).
 */
async function openFirstWorkingPort(preferredIdx, staleToClose) {
  let ports;
  try {
    ports = await navigator.serial.getPorts();
  } catch (_) {
    return null;
  }
  if (!ports || ports.length === 0) return null;

  let savedKey = null;
  try { savedKey = localStorage.getItem(USB_KEY); } catch (_) {}

  const base = Number.isFinite(preferredIdx) ? preferredIdx : 0;
  const primary = Math.min(Math.max(base, 0), ports.length - 1);

  // (1) Znamy tozsamosc USB -> lacz sie tylko z pasujacym portem.
  if (savedKey) {
    const matches = [];
    for (let i = 0; i < ports.length; i++) {
      if (portUsbKey(ports[i]) === savedKey) matches.push(i);
    }
    if (matches.length === 0) return null;
    const ordered = matches.includes(primary)
      ? [primary, ...matches.filter(i => i !== primary)]
      : matches;
    for (const idx of ordered) {
      const p = ports[idx];
      if (!p) continue;
      try {
        if (await openPortIfNeeded(p, staleToClose)) {
          staleToClose = null;
          rememberPort(p, idx);
          return p;
        }
      } catch (_) { /* nastepny pasujacy */ }
    }
    return null;
  }

  // (2) Tozsamosci jeszcze nie znamy.
  const single = ports.length === 1;
  const order = [primary];
  for (let i = 0; i < ports.length; i++) {
    if (i !== primary) order.push(i);
  }

  for (const idx of order) {
    const p = ports[idx];
    if (!p) continue;
    try {
      if (!(await openPortIfNeeded(p, staleToClose))) continue;
      staleToClose = null;
      // Jeden port -> ufamy. Wiele -> to drukarka tylko gdy odpowiada na <dle/>.
      if (single || await portResponds(p)) {
        rememberPort(p, idx);
        return p;
      }
      // Otworzyl sie, ale nie odpowiada -> to nie ta drukarka. Zamknij i dalej.
      await closeQuietly(p);
    } catch (_) {
      // Ten port nie zadzialal - sprobuj nastepny.
    }
  }
  return null;
}

export async function reopenPort(port) {
  const target = port || mon.port;

  if (target && target.readable && target.writable) return target;

  const savedIdx = parseInt(localStorage.getItem('fiscal_printer_port_index') || '0', 10);
  // target to zwykle STARY, martwy obiekt po wypieciu USB - przekaz go do
  // zamkniecia, zeby zwolnic urzadzenie przed ponownym open().
  const stale = (target && !(target.readable && target.writable)) ? target : null;
  return openFirstWorkingPort(savedIdx, stale);
}

/**
 * Alias czytelny w kontekscie "przed drukiem sprawdz czy port pisze".
 */
export async function ensurePortWritable(port) {
  return reopenPort(port);
}

async function tryReconnect(reason) {
  if (mon.reconnecting) return null;
  mon.reconnecting = true;
  setStatus({ reconnecting: true, error: reason });
  try {
    // Po wpieciu USB urzadzenie potrzebuje chwili, a pierwszy open() czesto pada.
    // Ponawiamy kilka razy z rosnacym odstepem, zamiast poddac sie po 1 probie.
    const MAX_ATTEMPTS = 4;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      const reopened = await reopenPort(mon.port);
      if (reopened) {
        mon.port = reopened;
        setStatus({ connected: true, writable: true, reconnecting: false, error: null, lastBeat: Date.now() });
        return reopened;
      }
      if (attempt < MAX_ATTEMPTS) await delay(400 * attempt);
    }
    setStatus({ connected: false, writable: false, reconnecting: false });
    return null;
  } finally {
    mon.reconnecting = false;
  }
}

// ---- Serial device events ----------------------------------------------

function onSerialDisconnect(event) {
  if (mon.port && event.target === mon.port) {
    // Urzadzenie fizycznie zniklo - tylko oznacz rozlaczenie. NIE odpalamy tu
    // petli reconnect: probowanie open() gdy USB jest wypiete i tak padnie, a
    // zajeloby flage mon.reconnecting i zablokowalo obsluge zdarzenia 'connect'
    // przy ponownym wpieciu. Odzysk robi onSerialConnect (replug) + heartbeat.
    setStatus({ connected: false, writable: false, error: 'Drukarka rozlaczona (USB)' });
  }
}

function onSerialConnect(event) {
  // W trakcie druku NIE ruszamy portu (jest zasobem wylacznym) - druk sam odzyska
  // stan po zakonczeniu. To gwarantuje, ze ta sciezka NIE wysyla nic do portu
  // podczas fiskalizacji.
  if (mon.printing) return;
  // Ponowne wpiecie USB. NIE wymagamy event.target === mon.port — Chrome po
  // replug potrafi dac NOWY obiekt SerialPort.
  if (mon.port) {
    // Mielismy juz port (byl podlaczony) -> odzyskaj go (reopenPort dobierze
    // wlasciwy port po indeksie, jesli stary obiekt jest juz nieaktualny).
    tryReconnect('serial-connect');
  } else {
    // Nie mielismy jeszcze zadnego portu. To sytuacja z porannego problemu:
    // drukarka wystartowala PO nieudanym auto-connect (zimny start, USB enumeruje
    // sie z opoznieniem). Zdarzenie 'connect' oznacza "urzadzenie wlasnie sie
    // pojawilo" -> sprobuj je teraz podlaczyc, zamiast czekac na reczne dzialanie.
    tryAdoptNewPort('serial-connect-fresh');
  }
}

/**
 * Podlacz port, ktory pojawil sie w systemie, gdy jeszcze niczego nie
 * monitorowalismy (drukarka wlaczona po starcie strony). Wykrywa go tak samo jak
 * auto-connect (openFirstWorkingPort: dopasowanie po VID:PID / odpytanie), wiec
 * zly port nie zostanie zaadoptowany. Uruchamiana TYLKO gdy nie drukujemy i nie
 * mamy zywego portu - nigdy w trakcie fiskalizacji.
 */
async function tryAdoptNewPort(reason) {
  if (mon.reconnecting || mon.printing) return;
  if (mon.port && mon.port.readable && mon.port.writable) return; // juz mamy zywy port
  mon.reconnecting = true;
  setStatus({ reconnecting: true, error: reason });
  try {
    const savedIdx = parseInt(localStorage.getItem('fiscal_printer_port_index') || '0', 10);
    const opened = await openFirstWorkingPort(savedIdx, null);
    if (opened) {
      mon.port = opened;
      startHeartbeat();
      setStatus({ connected: true, writable: true, reconnecting: false, error: null, lastBeat: Date.now() });
    } else {
      setStatus({ connected: false, writable: false, reconnecting: false });
    }
  } finally {
    mon.reconnecting = false;
  }
}

function installDeviceListeners() {
  if (mon.disconnectHandler || typeof navigator === 'undefined' || !navigator.serial) return;
  mon.disconnectHandler = onSerialDisconnect;
  mon.connectHandler = onSerialConnect;
  try {
    navigator.serial.addEventListener('disconnect', mon.disconnectHandler);
    navigator.serial.addEventListener('connect', mon.connectHandler);
  } catch (_) { /* ignore */ }
}

/**
 * Rozpocznij monitorowanie danego portu (heartbeat + nasluch USB + status).
 */
export function startMonitoring(port) {
  mon.port = port;
  setStatus({
    connected: !!(port && port.readable && port.writable),
    writable: !!(port && port.writable),
    error: null,
  });
  installDeviceListeners();
  startHeartbeat();
}

/**
 * Zatrzymaj monitorowanie (przy rozlaczeniu przez uzytkownika).
 */
export function stopMonitoring() {
  stopHeartbeat();
  mon.port = null;
  setStatus({ connected: false, writable: false, reconnecting: false, error: null });
}

// ---- Public connect API -------------------------------------------------

/**
 * Check whether Web Serial API is available in this browser.
 */
export function isSupported() {
  return 'serial' in navigator;
}

/**
 * Auto-connect to the last remembered serial port.
 * Returns { success, message, alreadyConnected? }
 */
export async function autoConnect(state) {
  if (!isSupported()) {
    return { success: false, message: 'Web Serial API nie wspierane' };
  }

  // Uzbroj nasluch USB OD RAZU, nawet zanim (i jesli) uda sie polaczyc. Inaczej
  // drukarka wlaczona PO nieudanym auto-connect (zimny start, USB z opoznieniem)
  // nie zostalaby wychwycona - zdarzenie 'connect' bylo dotad sluchane dopiero po
  // pierwszym udanym polaczeniu. Teraz onSerialConnect zaadoptuje ja automatycznie.
  // installDeviceListeners jest idempotentne (guard w srodku).
  installDeviceListeners();

  if (state.connected && state.port && state.port.readable && state.port.writable) {
    startMonitoring(state.port);
    return { success: true, message: 'Juz polaczono', alreadyConnected: true };
  }

  let ports;
  try {
    ports = await navigator.serial.getPorts();
  } catch (error) {
    return { port: null, connected: false, success: false, message: error.message };
  }

  // Brak nadanych portow = uzytkownik nigdy nie wybral drukarki w tej przegladarce.
  // Rozrozniamy to od "nie udalo sie otworzyc", bo hook nie ma sensu ponawiac.
  if (!ports || ports.length === 0) {
    return { success: false, message: 'Brak zapamietanych portow' };
  }

  // Skanuj wszystkie nadane porty (preferujac zapamietany) i wez ten, ktory
  // realnie sie otwiera - po twardym reloadzie / replug drukarka moze trafic
  // na inny port systemowy.
  const savedIdx = parseInt(localStorage.getItem('fiscal_printer_port_index') || '0', 10);
  const opened = await openFirstWorkingPort(savedIdx, null);
  if (opened) {
    startMonitoring(opened);
    return { port: opened, connected: true, success: true, message: 'Automatycznie polaczono z drukarką' };
  }

  return { port: null, connected: false, success: false, message: 'Nie udalo sie otworzyc zadnego portu' };
}

/**
 * Zapamietaj wybrany port do przyszlego auto-connect: indeks ORAZ tozsamosc USB
 * (VID:PID). Dzieki VID:PID przy nastepnym starcie od razu trafiamy we wlasciwy
 * fizyczny sprzet (np. COM4), nawet gdy system nada mu inny numer COM i bez
 * odpytywania go statusem.
 */
export async function savePortIndex(port) {
  try {
    const ports = await navigator.serial.getPorts();
    const currentIndex = ports.indexOf(port);
    if (currentIndex >= 0) {
      localStorage.setItem('fiscal_printer_port_index', currentIndex.toString());
    }
  } catch (_) {
    // failed to save port index
  }
  try {
    const key = portUsbKey(port);
    if (key) localStorage.setItem(USB_KEY, key);
  } catch (_) { /* getInfo niedostepne - trudno, zostaje sam indeks */ }
}

/**
 * Request a port from the user (manual selection) and open it.
 * Returns { port, connected, success, message }
 */
export async function connect() {
  if (!isSupported()) {
    throw new Error('Web Serial API nie jest wspierane w tej przegladarce. Uzywaj Chrome lub Edge.');
  }

  const port = await navigator.serial.requestPort();
  await port.open(SERIAL_PORT_OPTIONS);

  // WERYFIKACJA portu: czy jest tu NAPRAWDE drukarka. Bez tego wybor zlego portu
  // COM konczyl sie falszywym "Polaczono" (otwiera sie, ale nie drukuje) i zapisem
  // VID:PID zlego urzadzenia jako tozsamosci drukarki, co psulo auto-wykrywanie.
  const responds = await portResponds(port);
  // UTWARDZENIE (przyczyna regresji z 2026-07-21): po sondzie OPROZNIJ bufor.
  // Odpowiedz na <dle/> nie ma pola lasttransactioncorrect; gdyby spozniony/
  // powtorzony <dle> zostal w porcie, odczyt potwierdzenia PIERWSZEGO paragonu
  // zlapalby go i uznal paragon za "niezafiskalizowany" mimo wydruku. Drenaz
  // dłuzszy niz czas odpowiedzi (<0.4s) gwarantuje czysty port przed drukiem.
  await drainPort(port, 800);
  if (!responds) {
    await closeQuietly(port);
    return {
      port: null,
      connected: false,
      success: false,
      message: 'Na wybranym porcie nie wykryto drukarki fiskalnej (brak odpowiedzi). Wybierz inny port.',
    };
  }

  await savePortIndex(port);
  startMonitoring(port);
  return { port, connected: true, success: true, message: 'Polaczono z drukarką' };
}

/**
 * Close the current port and reset state.
 */
export async function disconnect(port) {
  stopMonitoring();
  try {
    if (port) {
      await port.close();
    }
    // disconnected
  } catch (error) {
    console.error('Drukarka: blad rozlaczania:', error);
  }
}
